import { getStage } from "src/context";
import { useViewport } from "src/contexts";
import {
  ConfiguratorContext_CheckoutOptionGroupFragment,
  ToggleOption_CheckoutConfigOptionGroupFragment,
  ToggleOption_CheckoutConfigPotentialOptionFragment,
} from "src/fragments/circularFragments.generated";
import { calcSelectedOptionIds } from "src/pages/configurator/calcSelectedOptionIds";
import { CutoffDaysRemaining } from "src/pages/configurator/components/options";
import { useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { getCutoffDaysRemaining, shouldUseSwatchSelector } from "src/pages/configurator/Configurator.utils";
import { ProductDetails_CheckoutConfigPotentialOptionFragment } from "src/pages/configurator/OptionProductDetails.generated";
import { InteriorStep_CheckoutOptionGroupFragment } from "src/pages/configurator/stepTemplates/Interior.step.generated";
import {
  Carousel,
  ConflictTooltip,
  DebugText,
  FlexibleOptionCard,
  LoadingBone,
  LoadingCarousel,
  OptionPreviewImage,
  OptionPreviewImageLoader,
  OptionThumbnail,
  Radio,
} from "~components";
import { Css } from "~generated/css";
import { ReadyPlanOptionGroupPersonalizationType } from "~generated/graphqlTypes";
import { formatDeltaPriceDisplay, titleCase, useTestIds } from "~utils";
import {
  ProductAddOnCard_CheckoutConfigOptionGroupFragment,
  ProductAddOnCard_CheckoutConfigPotentialOptionFragment,
} from "./ProductAddOnCard.generated";

// Helper to always show images fallback image if we're working locally outside of `yarn start-qa`
// Our local backend doesn't return image URLs
const forceValidImages = getStage() === "local";

type ProductAddOnCardProps = {
  onSelect: (
    group: ToggleOption_CheckoutConfigOptionGroupFragment,
    option: ToggleOption_CheckoutConfigPotentialOptionFragment,
  ) => void;
  disabled?: boolean;
  // Internally sets selected state based on selectedOptionIds instead of checking option.isSelected
  // for instant feedback
  selectedOptionIds: string[];
  ccfgOptionGroup: InteriorStep_CheckoutOptionGroupFragment;
  allOptionGroups: ConfiguratorContext_CheckoutOptionGroupFragment[];
  autoSaving: boolean;
  onCompare?: (ccog?: InteriorStep_CheckoutOptionGroupFragment) => void;
  hideCutoff?: boolean;
  displayPackageOptionsAsSingleProduct?: boolean;
  // Returns option selector (swatch carousel) only
  asChild?: boolean;
};

/**
 * Renders a selectable option card with preview image.
 *
 * Multioption groups will render a swatch carousel selector.
 *
 * Singleoption groups will render a radio button.
 *
 * Expects to only ever render a *single* child option as a `<FlexibleOptionCard />`
 */
export function ProductAddOnCard(props: ProductAddOnCardProps) {
  const {
    selectedOptionIds,
    onSelect,
    disabled = false,
    ccfgOptionGroup,
    allOptionGroups,
    autoSaving,
    displayPackageOptionsAsSingleProduct = false,
    onCompare = () => {},
    hideCutoff = false,
    asChild = false,
  } = props;
  const { group, options, cutoffDate, childGroup } = ccfgOptionGroup;
  const { required, isMultiOptionGroup, isSingleOptionGroup, readyPlanLocation } = group;
  const selectedOption = options.find((o) => selectedOptionIds.includes(o.option.id)) ?? options[0];
  const { isDesktop } = useViewport();
  const { getProductDetails, productDetailsLoading } = useConfiguratorContext();

  const { removedOptions } = calcSelectedOptionIds(allOptionGroups, selectedOptionIds, ccfgOptionGroup, selectedOption);

  // We need the full ccog to get access to both rpog and options so we pull it directly from allOptionGroups
  const optionGroupChild = allOptionGroups.find((og) => og.group.id === childGroup?.id);

  const showChildAsSwatch = shouldUseSwatchSelector(optionGroupChild);

  const selected = selectedOptionIds.includes(selectedOption.option.id);
  const showChild = !!optionGroupChild && selected;

  const selectedChildOption = optionGroupChild?.options.find((o) => selectedOptionIds.includes(o.option.id));
  // Swatch selectors don't show their price so we must roll it into the parent price that unlocked them
  const rollChildPriceIntoParent = !!selectedChildOption && shouldUseSwatchSelector(optionGroupChild);

  // Get product details from context - loaded async by secondary query
  const selectedOptionProductDetails = getProductDetails(selectedOption.id);
  const selectedOptionMaterialVariant = selectedOptionProductDetails?.option.materialVariant;

  // Check if this specific option group has valid images
  const hasValidOptionImages =
    forceValidImages ||
    options.some((o) => o.option.optionImage?.thumbnail155) ||
    (displayPackageOptionsAsSingleProduct &&
      !productDetailsLoading &&
      options.some((o) => !!getProductAddOnCardImageUrl(getProductDetails(o.id)))) ||
    (selectedChildOption && selectedChildOption.option.optionImage?.thumbnail155);

  // We have to align the radio button based on if we render the brand-cutoff row or not
  // When we have brand but no cutoff
  // - The radio must align with the price label and that must align with the option name label
  // When we have cutoff but no brand
  // - The radio must center align between the price and cutoff labels
  // - The price label must align with the option name label
  // in any case where the option name wraps we must ensure that the price label is aligned before the line break, not centered
  // Note: Brand label requires materialVariant which is loaded async
  const hasBrandLabel = !productDetailsLoading && !!selectedOptionMaterialVariant?.listing?.brand?.name;
  const hasCutoffLabel = !hideCutoff && !!cutoffDate && getCutoffDaysRemaining(cutoffDate) <= 21;
  const hasBrandCutoffRow = hasBrandLabel || hasCutoffLabel;

  const groupDescription = getDescription(
    group,
    selectedOption,
    displayPackageOptionsAsSingleProduct,
    selectedOptionProductDetails,
  );
  const showConflictTooltip =
    !disabled && removedOptions.nonEmpty && removedOptions.every((o) => o.id !== selectedChildOption?.id);

  const tid = useTestIds(props, "product-add-on-card");

  // Async product loading only applies to package options here via `displayPackageOptionsAsSingleProduct`
  const showLoadingCarousel = displayPackageOptionsAsSingleProduct && productDetailsLoading;

  const OptionSelector = showLoadingCarousel ? (
    <LoadingCarousel count={4} size={40} />
  ) : (
    <Carousel
      increment={3}
      items={options.map((o) => {
        const imageUrl = displayPackageOptionsAsSingleProduct
          ? getProductAddOnCardImageUrl(getProductDetails(o.id))
          : o.option.optionImage?.thumbnail155;
        return (
          <OptionThumbnail
            key={o.option.id}
            thumbnailId={o.option.id}
            disabled={disabled}
            optionName={o.option.name}
            imageUrl={imageUrl}
            debugText={asChild ? `${o.option.id} - price: ${o.totalPriceInCents}` : `${o.option.id}`}
            isSelected={selectedOptionIds.includes(o.option.id)}
            onClick={() => onSelect(ccfgOptionGroup, o)}
          />
        );
      })}
    />
  );

  if (asChild) {
    return OptionSelector;
  }

  return (
    <div
      id="product-add-on-card"
      css={{
        ...Css.py3.w100.br4.df.fdc.aic.add("transition", "0.2s").if(showChild).gap1.else.gap2.$,
        // If disabled, we want to show the normal state but not allow interaction
        ...Css.if(disabled).cursorNotAllowed.if(disabled && !selected).o25.$,
        // Keeping this logic out of the above if else onHover logic
        // unfortunately we have to add `isDesktop` check instead of `Css.ifDesktopAndUp.onHover` because truss doesnt handle it properly.
        ...Css.if(isDesktop && isSingleOptionGroup && !disabled && !showChild).cursorPointer.onHover.bgGray100.$,
      }}
      onClick={() => {
        if (!isMultiOptionGroup && !disabled && !showChild) {
          onSelect(ccfgOptionGroup, selectedOption);
        }
      }}
      {...tid}
    >
      {isMultiOptionGroup && (
        <div css={Css.t16.df.asfs.if(displayPackageOptionsAsSingleProduct).asStretch.jcsb.$}>
          <div>{group.name}</div>
          {displayPackageOptionsAsSingleProduct && (
            <button type="button" onClick={() => onCompare(ccfgOptionGroup)} css={Css.t10.gray.cursorPointer.$}>
              Compare
            </button>
          )}
        </div>
      )}
      <div css={Css.df.gap2.w100.$}>
        {/* Show loading state for image when product details are loading */}
        {productDetailsLoading && displayPackageOptionsAsSingleProduct ? (
          <OptionPreviewImageLoader size={96} />
        ) : (
          hasValidOptionImages && (
            <OptionPreviewImage
              size={96}
              optionName={selectedOption.option.name}
              imageUrl={
                displayPackageOptionsAsSingleProduct
                  ? getProductAddOnCardImageUrl(selectedOptionProductDetails)
                  : (selectedOption.option.optionImage?.thumbnail155 ??
                    selectedChildOption?.option.optionImage?.thumbnail155)
              }
            />
          )
        )}
        <div css={Css.df.w100.fdc.gap1.$}>
          <div css={Css.df.gap1.$}>
            {/* Header Area */}
            <div css={Css.df.jcsb.f1.gap1.fs0.if(!hasBrandCutoffRow).aifs.$}>
              {/* Brand and name column */}
              <div css={Css.df.fdc.gapPx(2).$}>
                <div css={Css.t10.gray.if(hasBrandCutoffRow).mhPx(17).$}>
                  {titleCase(selectedOptionMaterialVariant?.listing?.brand?.name ?? "")}
                </div>
                <div css={Css.t14.black.$} {...tid.selectedOptionName}>
                  <DebugText>
                    {group.id} {group.name}
                  </DebugText>
                  {selectedOption?.option.name}
                  {/* Only show the conflict information if it's not for the child option and the card is not disabled */}
                  {showConflictTooltip && (
                    <ConflictTooltip
                      conflicts={removedOptions}
                      optionName={selectedOption?.option.name}
                      isAlreadySelected={selected}
                    />
                  )}
                </div>
              </div>
              {/* Cutoff, pricing, radio column */}
              <div css={Css.df.gap1.hminc.aic.$}>
                <div css={Css.df.fdc.tar.gapPx(2).$}>
                  <div css={Css.if(hasBrandCutoffRow).mhPx(17).$}>
                    {!hideCutoff && <CutoffDaysRemaining cutoffDate={cutoffDate} />}
                  </div>
                  <PricingLabel
                    selected={selected}
                    autoSaving={autoSaving}
                    visualOption={selectedOption}
                    selectedChildOption={rollChildPriceIntoParent && selectedChildOption}
                  />
                </div>
                {isSingleOptionGroup && (
                  <div css={Css.df.if(!hasCutoffLabel && hasBrandLabel).mtPx(15).$}>
                    <Radio
                      selected={!!selected}
                      disabled={disabled}
                      variant={required ? "primary" : "primaryPlus"}
                      onClick={() => onSelect(ccfgOptionGroup, selectedOption)}
                      {...tid.checkbox}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Main body */}
          {readyPlanLocation?.marketingName && (
            <div css={Css.t10.gray.$}>
              Location: {readyPlanLocation.marketingName}
              <DebugText> ({readyPlanLocation.location?.name})</DebugText>
            </div>
          )}
          <div css={Css.t12.gray.lineClamp2.$}>
            {/* Fill empty space with the child option name if we have no description to show */}
            {groupDescription ?? selectedChildOption?.option.name ?? ""}
          </div>
        </div>
      </div>
      {isMultiOptionGroup && (hasValidOptionImages || showLoadingCarousel) && (
        <div css={Css.w100.$}>{OptionSelector}</div>
      )}
      {showChild && (
        <div
          key={optionGroupChild.group.id}
          // Adding o25 when disabled here allows the additional child options to pop in more smoothly when card is stacked
          // on top of PackageOption
          css={Css.df.fdc.gap1.w100.if(disabled && !showChildAsSwatch).o25.$}
        >
          {/* Child packages need access to the compare packages modal */}
          {optionGroupChild.group.personalizationType?.code === ReadyPlanOptionGroupPersonalizationType.Package && (
            <button
              type="button"
              onClick={() => onCompare(optionGroupChild)}
              css={Css.t10.gray.cursorPointer.asfe.py1.$}
            >
              Compare {group.name}
            </button>
          )}
          <FlexibleOptionCard
            ccfgOptionGroup={optionGroupChild}
            onCompare={() => onCompare(optionGroupChild)}
            asChild={true}
          />
        </div>
      )}
    </div>
  );
}

function getProductAddOnCardImageUrl(
  productDetails: ProductDetails_CheckoutConfigPotentialOptionFragment | undefined,
): string | undefined {
  if (!productDetails) return undefined;

  return productDetails.primaryProduct?.featuredImage?.asset?.thumbnail155 ?? undefined;
}

function getDescription(
  group: ProductAddOnCard_CheckoutConfigOptionGroupFragment,
  option: ProductAddOnCard_CheckoutConfigPotentialOptionFragment,
  displayPackageOptionsAsSingleProduct: boolean,
  productDetails: ProductDetails_CheckoutConfigPotentialOptionFragment | undefined,
) {
  // Just like PackageOptionCard
  if (displayPackageOptionsAsSingleProduct) {
    return group.marketingDescription;
  }

  const materialVariant = productDetails?.option.materialVariant;

  // Note: Only applicable to Design Package-owned personalization RPOGs. Otherwise we'll always use the global option
  // marketing description default.
  switch (group.personalizationType?.code) {
    case ReadyPlanOptionGroupPersonalizationType.SingleProduct:
      return option.option?.description ?? materialVariant?.listing?.description;
    case ReadyPlanOptionGroupPersonalizationType.LinkedProduct:
      return (
        group.marketingDescription ?? option.option?.description ?? option.option.globalOption?.marketingDescription
      );
    default:
      return option.option.globalOption?.marketingDescription;
  }
}

function PricingLabel(props: {
  selected: boolean;
  autoSaving: boolean;
  visualOption?: ProductAddOnCard_CheckoutConfigPotentialOptionFragment;
  selectedChildOption?: ProductAddOnCard_CheckoutConfigPotentialOptionFragment | false;
}) {
  const { selected, autoSaving, visualOption, selectedChildOption } = props;

  const summedPriceInCents =
    (visualOption?.totalPriceInCents ?? 0) + (selectedChildOption ? selectedChildOption.totalPriceInCents : 0);

  return (
    <span
      css={{
        ...Css.gap1.t14.wsnw.df.jcfe.$,
        ...(selected && Css.gray800.fw500.$),
      }}
    >
      {autoSaving ? <LoadingBone /> : formatDeltaPriceDisplay(summedPriceInCents)}
    </span>
  );
}

export function SkeletonProductAddOnLoaderCard() {
  return (
    <div
      css={{
        ...Css.py3.w100.gap2.br4.df.fdc.aic.add("transition", "0.2s").$,
      }}
    >
      <div css={Css.df.gap2.w100.$}>
        <OptionPreviewImageLoader size={96} />
        <div css={Css.df.w100.fdc.gap2.$}>
          <div css={Css.df.gap1.$}>
            {/* Header Area */}
            <div css={Css.df.jcsb.f1.fs0.aic.$}>
              {/* Brand and name column */}
              <div css={Css.df.fdc.gapPx(2).$}>
                <div css={Css.t14.black.$}>
                  <LoadingBone width="90px" />
                </div>
              </div>
              {/* Cutoff, pricing, radio column */}
              <div css={Css.df.gap1.hminc.aic.$}>
                <div css={Css.df.fdc.tar.gapPx(2).$}>
                  <LoadingBone width="70px" />
                </div>
                <div css={Css.df.$}>
                  <Radio selected={false} disabled />
                </div>
              </div>
            </div>
          </div>
          {/* Main body */}
          <div css={Css.t12.gray.df.fdc.gap1.$}>
            <LoadingBone width="100%" />
            <LoadingBone width="100%" />
          </div>
        </div>
      </div>
    </div>
  );
}
